/**
 * Documents Service
 *
 * Multi-tenant aware service for BOL/POD document management.
 * Documents are stored in:
 * - GCS: gs://{storageBucket}/documents/tenants/{tenantId}/bols/{timestamp}_{filename}
 * - Firestore: tenants/{tenantId}/documents
 *
 * Handles document upload, listing, linking to loads/notes, and real-time updates.
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL
} from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FirebaseService } from '../../../shared/services/FirebaseService';

// Use default Firebase Storage bucket (env-configured per FirebaseService)

class DocumentsServiceClass {
  constructor() {
    this.db = null;
    this.storage = null;
    this.functions = null;
    this.tenantId = null;
    this.initialized = false;
    console.log('DocumentsService initialized');
  }

  /**
   * Set the current tenant context
   * @param {string} tenantId - The tenant ID (e.g., 'inmocean', 'demo')
   */
  setTenant(tenantId) {
    if (!tenantId) {
      throw new Error('tenantId is required');
    }
    this.tenantId = tenantId;
    console.log(`DocumentsService tenant set to: ${tenantId}`);
  }

  /**
   * Get current tenant ID
   */
  getTenantId() {
    if (!this.tenantId) {
      throw new Error('Tenant not set. Call setTenant(tenantId) first.');
    }
    return this.tenantId;
  }

  /**
   * Initialize Firebase connections
   */
  async initialize() {
    if (this.initialized && this.db && this.storage) return;

    try {
      await FirebaseService.initialize();
      this.db = FirebaseService.getFirestore();
      // Use default Firebase Storage bucket
      this.storage = FirebaseService.getStorage();
      this.functions = getFunctions(FirebaseService.getApp(), 'us-central1');
      this.initialized = true;
      console.log('DocumentsService Firebase initialized');
    } catch (error) {
      console.error('Failed to initialize DocumentsService:', error);
      throw error;
    }
  }

  /**
   * Get the collection reference for current tenant's documents
   */
  getDocumentsCollection() {
    const tenantId = this.getTenantId();
    return collection(this.db, `tenants/${tenantId}/documents`);
  }

  /**
   * Upload a document (BOL or POD)
   * @param {File} file - The file to upload
   * @param {string} type - Document type: 'bol' or 'pod'
   * @returns {Object} Upload result with storage path
   */
  async uploadDocument(file, type = 'bol') {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      console.log(`Uploading ${type} document: ${file.name}`);

      // Generate unique filename with timestamp
      const timestamp = Date.now();
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const storagePath = `documents/tenants/${tenantId}/bols/${timestamp}_${sanitizedName}`;

      // Upload to GCS via Firebase Storage (default bucket per FirebaseService config)
      const defaultBucketRef = ref(this.storage, storagePath);

      const snapshot = await uploadBytes(defaultBucketRef, file, {
        contentType: file.type,
        customMetadata: {
          tenantId,
          documentType: type,
          originalName: file.name,
          uploadedBy: FirebaseService.getCurrentUser()?.email || 'unknown'
        }
      });

      console.log(`File uploaded successfully: ${storagePath}`);

      // The Cloud Function will process the file and create the Firestore document
      // Return the storage path for tracking
      return {
        success: true,
        storagePath,
        fileName: file.name,
        type
      };

    } catch (error) {
      console.error('Failed to upload document:', error);
      throw new Error(`Failed to upload document: ${error.message}`);
    }
  }

  /**
   * Get all documents for the current tenant
   * @returns {Array} Array of all documents
   */
  async getAllDocuments() {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      console.log(`Fetching all documents for tenant ${tenantId}`);

      const docsRef = this.getDocumentsCollection();
      const q = query(docsRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);

      const documents = [];
      querySnapshot.forEach((doc) => {
        documents.push({ id: doc.id, ...doc.data() });
      });

      console.log(`Retrieved ${documents.length} documents`);
      return documents;

    } catch (error) {
      console.error('Failed to get all documents:', error);
      throw error;
    }
  }

  /**
   * Get a document by its ID
   * @param {string} docId - The document ID
   * @returns {Object|null} The document or null if not found
   */
  async getDocumentById(docId) {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      const docRef = doc(this.db, `tenants/${tenantId}/documents`, docId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return { id: docSnap.id, ...docSnap.data() };

    } catch (error) {
      console.error('Failed to get document:', error);
      throw error;
    }
  }

  /**
   * Get documents by PO number
   * @param {string} po - The PO number
   * @returns {Array} Array of documents for this PO
   */
  async getDocumentsByPO(po) {
    await this.initialize();

    try {
      const docsRef = this.getDocumentsCollection();
      const q = query(docsRef, where('matchedPo', '==', po));
      const querySnapshot = await getDocs(q);

      const documents = [];
      querySnapshot.forEach((doc) => {
        documents.push({ id: doc.id, ...doc.data() });
      });

      return documents;

    } catch (error) {
      console.error('Failed to get documents by PO:', error);
      throw error;
    }
  }

  /**
   * Get documents by Load ID
   * @param {string} loadId - The Load ID
   * @returns {Array} Array of documents for this load
   */
  async getDocumentsByLoadId(loadId) {
    await this.initialize();

    try {
      const docsRef = this.getDocumentsCollection();
      const q = query(docsRef, where('matchedLoadId', '==', loadId));
      const querySnapshot = await getDocs(q);

      const documents = [];
      querySnapshot.forEach((doc) => {
        documents.push({ id: doc.id, ...doc.data() });
      });

      return documents;

    } catch (error) {
      console.error('Failed to get documents by Load ID:', error);
      throw error;
    }
  }

  /**
   * Get documents by status
   * @param {string} status - Status: 'processing', 'matched', 'unmatched', 'error'
   * @returns {Array} Array of documents with this status
   */
  async getDocumentsByStatus(status) {
    await this.initialize();

    try {
      const docsRef = this.getDocumentsCollection();
      const q = query(
        docsRef,
        where('status', '==', status),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);

      const documents = [];
      querySnapshot.forEach((doc) => {
        documents.push({ id: doc.id, ...doc.data() });
      });

      return documents;

    } catch (error) {
      console.error('Failed to get documents by status:', error);
      throw error;
    }
  }

  /**
   * Get documents linked to a note
   * @param {string} noteId - The note ID
   * @returns {Array} Array of documents linked to this note
   */
  async getDocumentsByNoteId(noteId) {
    await this.initialize();

    try {
      const docsRef = this.getDocumentsCollection();
      const q = query(docsRef, where('linkedNoteId', '==', noteId));
      const querySnapshot = await getDocs(q);

      const documents = [];
      querySnapshot.forEach((doc) => {
        documents.push({ id: doc.id, ...doc.data() });
      });

      return documents;

    } catch (error) {
      console.error('Failed to get documents by note ID:', error);
      throw error;
    }
  }

  /**
   * Link a document to a note
   * @param {string} docId - The document ID
   * @param {string} noteId - The note ID to link to
   */
  async linkToNote(docId, noteId) {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      console.log(`Linking document ${docId} to note ${noteId}`);

      const linkDocToNote = httpsCallable(this.functions, 'linkDocumentToNote');
      const result = await linkDocToNote({ tenantId, docId, noteId });

      console.log('Document linked to note successfully');
      return result.data;

    } catch (error) {
      console.error('Failed to link document to note:', error);
      throw new Error(`Failed to link document: ${error.message}`);
    }
  }

  /**
   * Manually match a document to a load
   * @param {string} docId - The document ID
   * @param {string} loadId - The load ID to match
   * @param {string} po - The PO number to match
   */
  async manualMatch(docId, loadId, po) {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      console.log(`Manually matching document ${docId} to load ${loadId}, PO ${po}`);

      const linkDocToLoad = httpsCallable(this.functions, 'linkDocumentToLoad');
      const result = await linkDocToLoad({
        tenantId,
        docId,
        loadId: loadId || null,
        poNumber: po || null
      });

      console.log('Document matched successfully');
      return result.data;

    } catch (error) {
      console.error('Failed to manually match document:', error);
      throw new Error(`Failed to match document: ${error.message}`);
    }
  }

  /**
   * Get a signed URL for viewing/downloading a document
   * @param {string} docId - The document ID
   * @returns {string} Signed URL for the document
   */
  async getViewUrl(docId) {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      const getDocUrl = httpsCallable(this.functions, 'getDocumentViewUrl');
      const result = await getDocUrl({ tenantId, docId });
      return result.data.url;

    } catch (error) {
      console.error('Failed to get document URL:', error);
      throw new Error(`Failed to get document URL: ${error.message}`);
    }
  }

  /**
   * Delete a document
   * @param {string} docId - The document ID to delete
   */
  async deleteDocument(docId) {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      console.log(`Deleting document ${docId}`);

      const deleteDoc = httpsCallable(this.functions, 'deleteDocument');
      const result = await deleteDoc({ tenantId, docId });

      console.log('Document deleted successfully');
      return result.data;

    } catch (error) {
      console.error('Failed to delete document:', error);
      throw new Error(`Failed to delete document: ${error.message}`);
    }
  }

  /**
   * Re-process a document (run OCR again)
   * @param {string} docId - The document ID to reprocess
   */
  async reprocessDocument(docId) {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      console.log(`Reprocessing document ${docId}`);

      const reprocess = httpsCallable(this.functions, 'reprocessDocument');
      const result = await reprocess({ tenantId, docId });

      console.log('Document reprocessed:', result.data);
      return result.data;

    } catch (error) {
      console.error('Failed to reprocess document:', error);
      throw new Error(`Failed to reprocess document: ${error.message}`);
    }
  }

  /**
   * Subscribe to real-time updates for all documents
   * @param {Function} callback - Callback function receiving documents array
   * @returns {Function} Unsubscribe function
   */
  subscribeToDocuments(callback) {
    if (!this.db) {
      console.error('Firestore not initialized. Call initialize() first.');
      return () => {};
    }

    const tenantId = this.getTenantId();
    console.log(`Subscribing to documents for tenant ${tenantId}`);

    const docsRef = this.getDocumentsCollection();
    const q = query(docsRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const documents = [];
      querySnapshot.forEach((doc) => {
        documents.push({ id: doc.id, ...doc.data() });
      });

      console.log(`Documents updated: ${documents.length} documents`);
      callback(documents);
    }, (error) => {
      console.error('Error listening to documents:', error);
    });

    return unsubscribe;
  }

  /**
   * Get unmatched documents (for manual linking UI)
   * @returns {Array} Array of unmatched documents
   */
  async getUnmatchedDocuments() {
    return this.getDocumentsByStatus('unmatched');
  }

  /**
   * Get document statistics
   * @returns {Object} Stats object with counts by status
   */
  async getDocumentStats() {
    const documents = await this.getAllDocuments();

    const stats = {
      total: documents.length,
      processing: 0,
      matched: 0,
      unmatched: 0,
      error: 0,
      byType: {
        bol: 0,
        pod: 0
      }
    };

    documents.forEach(doc => {
      if (doc.status) stats[doc.status]++;
      if (doc.documentType) stats.byType[doc.documentType]++;
    });

    return stats;
  }
}

// Export singleton instance
export const DocumentsService = new DocumentsServiceClass();

// Also export class for testing
export { DocumentsServiceClass };
